import React, { useCallback, useEffect, useState } from "react";
import {
  delete_notification,
  get_notifications,
} from "../../services/apis/notification";
import { useAppDispatch, useAppSelector } from "../../store";
import { real_date } from "../../services/convert/date";
import { Eye, Loader2, Mail, Trash2 } from "lucide-react";
import ViewNotification from "./view/View";
import Button from "../../component/element/forms/Button";
import EmailNotification from "../company/misc/EmailNotification";
import Paginate from "../../component/ui/paginate";

const Notification = () => {
  const dispatch = useAppDispatch();
  const { notifications, deletingIds, loading, limit, offset, total } =
    useAppSelector(({ notification }) => notification);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState(false);

  const [selectedNotification, setSelectedNotification] = useState<
    (typeof notifications)[0] | null
  >(null);

  const onLoad = useCallback(() => {
    get_notifications(dispatch, limit, offset);
  }, [dispatch, limit, offset]);

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  const handleViewNotification = (data: (typeof notifications)[0]) => {
    setSelectedNotification(data);
    setModalOpen(true);
  };

  const isDeleting = (id: number) => deletingIds === id;
  const currentPage = Math.floor(offset / limit) + 1;
  const lastPage = Math.max(1, Math.ceil(total / limit));

  const loadPage = (page: number) => {
    get_notifications(dispatch, limit, (page - 1) * limit);
  };

  // const isRead = notifications[0]?.seen === "1";

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-bold text-xl capitalize mb-0">Notifications</h2>
        <Button
          title="Post Notification"
          type="button"
          children={<Mail className="mr-2" />}
          onClick={() => setCreateOpen(true)}
          className="px-3"
        />
      </div>

      <div className="grid divide-y divide-gray-100 rounded-2xl bg-white overflow-hidden">
        {loading && notifications.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            Loading notifications...
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            No notifications found.
          </div>
        ) : (
          notifications.map((data, i) => (
            <div
              key={data.id}
              className="relative flex flex-col sm:flex-row sm:items-center gap-2 hover:bg-gray-100/50 transition-shadow duration-300 px-4 py-4"
            >
              <div className="sm:absolute sm:top-1/2 sm:left-4 sm:-translate-y-1/2 text-xs text-gray-600 font-medium w-8 h-8 bg-primary-100/50 rounded-full grid place-content-center">
                {offset + i + 1}
              </div>

              <div className="flex-1 flex items-center gap-4 sm:ml-8">
                <p className="text-md font-semibold text-gray-800 mb-0">
                  {data.subject}
                </p>
              </div>

              <div className="flex justify-between sm:justify-normal items-center gap-2 sm:ml-auto">
                <span className="text-xs text-gray-500">
                  {real_date(data.created_at)}
                </span>
                <div className="inline-flex items-center gap-2">
                  <Eye
                    className="size-4 cursor-pointer"
                    onClick={() => handleViewNotification(data)}
                  />
                  {isDeleting(data.id) ? (
                    <Loader2 className="size-4 text-red-500 animate-spin" />
                  ) : (
                    <Trash2
                      className="size-4 text-red-500 cursor-pointer"
                      onClick={() => delete_notification(data.id, dispatch)}
                    />
                  )}
                </div>
              </div>
            </div>
          ))
        )}

        <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Page <span className="font-medium text-gray-700">{currentPage}</span>{" "}
            of <span className="font-medium text-gray-700">{lastPage}</span>
          </p>
          <Paginate
            current_page={currentPage}
            last_page={lastPage}
            onNext={loadPage}
            onPrev={loadPage}
          />
        </div>
      </div>

      <ViewNotification
        isOpen={modalOpen}
        isClose={() => setModalOpen(false)}
        notification={selectedNotification}
      />
      <EmailNotification
        isOpen={createOpen}
        isClose={() => setCreateOpen(false)}
        onSuccess={() => get_notifications(dispatch, limit, offset)}
      />
    </div>
  );
};

export default Notification;
